import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DesktopLayout from '../../components/layout/DesktopLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getWishById, contactWish } from '../../services/wishesApi';
import type { ApiWish } from '../../types';
import './WishDetailPage.css';

const WishDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [wish, setWish] = useState<ApiWish | null>(null);
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  // Redirect to list page with query string on desktop
  useEffect(() => {
    if (window.innerWidth >= 768 && id) {
      navigate(`/wishes?id=${id}`);
      return;
    }
  }, [id, navigate]);

  useEffect(() => {
    const fetchWish = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const data = await getWishById(id);
        setWish(data);
      } catch (error) {
        console.error('Failed to fetch wish:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWish();
  }, [id]);

  // Handle countdown timer for redirect
  useEffect(() => {
    if (redirectCountdown === null) return;

    if (redirectCountdown <= 0) {
      navigate('/pricing');
      return;
    }

    const timer = setTimeout(() => {
      setRedirectCountdown(redirectCountdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  const handleContact = async () => {
    if (!id || !isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user has permission to contact (free plan users cannot contact)
    const userPlan = user?.plan || 'free';
    if (userPlan === 'free') {
      setContactMessage('聯絡許願者需要付費方案');
      // Start countdown for redirect
      setRedirectCountdown(3);
      return;
    }

    setContacting(true);
    setContactMessage('');

    try {
      const result = await contactWish(id);
      if (result.success) {
        setContactMessage('聯繫請求已發送！許願者將會收到您的聯繫資訊。');
      } else {
        setContactMessage(result.message || '聯繫失敗，請稍後再試。');
      }
    } catch {
      setContactMessage('聯繫失敗，請稍後再試。');
    } finally {
      setContacting(false);
    }
  };

  const handleCancelRedirect = () => {
    setRedirectCountdown(null);
    setContactMessage('');
  };

  if (loading) {
    return (
      <div className="wish-detail-page">
        <DesktopLayout>
        <main className="wish-detail-page__main">
          <div className="wish-detail-page__loading">載入中...</div>
        </main>
        </DesktopLayout>
      </div>
    );
  }

  if (!wish) {
    return (
      <div className="wish-detail-page">
        <DesktopLayout>
        <main className="wish-detail-page__main">
          <div className="wish-detail-page__not-found">
            <h2>找不到此許願</h2>
            <button onClick={() => navigate('/wishes')} className="wish-detail-page__back-btn">
              返回許願池
            </button>
          </div>
        </main>
        </DesktopLayout>
      </div>
    );
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      '維修': '汽車維修',
      '保養': '汽車保養',
      '美容': '汽車美容',
      '改裝': '汽車改裝',
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'open': '尋找中',
      'matched': '配對中',
      'closed': '已結案',
    };
    return labels[status] || status;
  };

  // 安全地格式化日期
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '未知';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '未知';
    if (date.getFullYear() === 1970) return '未知';
    return date.toLocaleString('zh-TW');
  };

  return (
    <div className="wish-detail-page">
      <DesktopLayout>

      <main className="wish-detail-page__main">
        <div className="wish-detail-page__container">
          {/* Back Button */}
          <button
            onClick={() => navigate('/wishes')}
            className="wish-detail-page__back-link"
          >
            ← 返回許願池
          </button>

          {/* Wish Detail Card */}
          <div className="wish-detail-card">
            <div className="wish-detail-card__header">
              <h1 className="wish-detail-card__title">{wish.title}</h1>
              <div className="wish-detail-card__meta">
                <span className={`wish-detail-card__status wish-detail-card__status--${wish.status}`}>
                  {getStatusLabel(wish.status)}
                </span>
                {wish.urgent && (
                  <span className="wish-detail-card__urgent">急件</span>
                )}
              </div>
            </div>

            {wish.description && (
              <div className="wish-detail-card__section">
                <h2 className="wish-detail-card__section-title">需求描述</h2>
                <p className="wish-detail-card__description">{wish.description}</p>
              </div>
            )}

            <div className="wish-detail-card__section">
              <h2 className="wish-detail-card__section-title">詳細資訊</h2>
              <div className="wish-detail-card__info-grid">
                <div className="wish-detail-card__info-item">
                  <span className="wish-detail-card__label">服務類別：</span>
                  <span>{getCategoryLabel(wish.category)}</span>
                </div>
                <div className="wish-detail-card__info-item">
                  <span className="wish-detail-card__label">地區：</span>
                  <span>{wish.region}</span>
                  {wish.district && <span> ({wish.district})</span>}
                </div>
                {wish.budget && (
                  <div className="wish-detail-card__info-item">
                    <span className="wish-detail-card__label">預算：</span>
                    <span>NT${wish.budget}</span>
                  </div>
                )}
                <div className="wish-detail-card__info-item">
                  <span className="wish-detail-card__label">發布者：</span>
                  <span>{wish.userName || '匿名'}</span>
                </div>
                <div className="wish-detail-card__info-item">
                  <span className="wish-detail-card__label">發布時間：</span>
                  <span>{formatDate(wish.createdAt)}</span>
                </div>
                <div className="wish-detail-card__info-item">
                  <span className="wish-detail-card__label">瀏覽次數：</span>
                  <span>{wish.viewCount}</span>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            {wish.status === 'open' && (
              <div className="wish-detail-card__section wish-detail-card__section--contact">
                {contactMessage && (
                  <div className={`wish-detail-card__message ${
                    contactMessage.includes('成功') ? 'success' : 'error'
                  }`}>
                    {redirectCountdown !== null ? (
                      <>
                        <p>{contactMessage}</p>
                        <p className="wish-detail-card__countdown">
                          將在 {redirectCountdown} 秒後導向至方案頁面...
                        </p>
                        <button
                          onClick={handleCancelRedirect}
                          className="wish-detail-card__cancel-redirect"
                        >
                          取消導向
                        </button>
                      </>
                    ) : (
                      contactMessage
                    )}
                  </div>
                )}
                {isAuthenticated ? (
                  <button
                    onClick={handleContact}
                    disabled={contacting || redirectCountdown !== null}
                    className="wish-detail-card__contact-btn"
                  >
                    {contacting ? '發送中...' : '聯繫許願者'}
                  </button>
                ) : (
                  <div className="wish-detail-card__login-prompt">
                    <p>請先登入以聯繫許願者</p>
                    <button
                      onClick={() => navigate('/login')}
                      className="wish-detail-card__login-btn"
                    >
                      前往登入
                    </button>
                  </div>
                )}
              </div>
            )}

            {wish.status !== 'open' && (
              <div className="wish-detail-card__section">
                <p className="wish-detail-card__closed-notice">
                  此許願已結案或配對中，無法再聯繫
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      </DesktopLayout>
    </div>
  );
};

export default WishDetailPage;
